import _ from "lodash";

// Utils.
import { mapIds } from "../../../helpers/utils";

// API.
import AdminClient from '../client/AdminClient';

class PrivateWealthTeamsService {
    static endpoint = '/v1/admin/private_wealth_teams';

    static async index(session, params) {
        const filterParams = this.parsedTeamFilterParams(params.filterParams);
        return AdminClient.get(`${this.endpoint}`, session,
            {
                ...params,
                filterParams: filterParams,
            }
        );
    }

    static async new(session) {
        return AdminClient.get(`${this.endpoint}/new`, session);
    }

    static async create(session, team) {
        return AdminClient.post(`${this.endpoint}`, session, this.parsedTeam({ ...team }));
    }

    static async update(teamId, session, team) {
        return AdminClient.put(`${this.endpoint}/${teamId}`, session, this.parsedTeam({ ...team, pwt_locations_attributes: team.locations, pwt_historical_blueprints_attributes: team.historical_blueprints }));
    }

    static async edit(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}/edit`, session);
    }

    static async show(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}`, session);
    }

    static async delete(teamId, session) {
        return AdminClient.delete(`${this.endpoint}/${teamId}`, session);
    }

    static async bulkDelete(ids, session) {
        return AdminClient.delete(`${this.endpoint}/bulk_destroy`, session, { ids });
    }

    static async changeLog(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}/change_log`, session);
    }

    static async notes(teamId, session) {
        return AdminClient.get(`/v1/admin/private_wealth_teams/${teamId}/notes`, session);
    }

    static async uploadAvatar(teamId, session, file) {
        const formData = new FormData();
        formData.append('avatar', file);
        return AdminClient.uploadFile(`${this.endpoint}/${teamId}/upload_avatar`, session, formData);
    }

    static async news(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}/related_news`, session);
    }

    static async updateNews(teamId, session, params) {
        return AdminClient.put(`${this.endpoint}/${teamId}/update_news`, session, { edited_news: params });
    }

    static async parentCompanyOptions(session, params) {
        return AdminClient.get(`${this.endpoint}/parent_company_options`, session, params);
    }

    static async getPreDraftConfig(session, params) {
        return AdminClient.get(`${this.endpoint}/get_pre_draft_config`, session, params);
    }

    static async updatePreDraftConfig(session, params) {
        return AdminClient.put(`${this.endpoint}/update_pre_draft_config`, session, params);
    }

    static async getUrlDenylist(session, params) {
        return AdminClient.get(`${this.endpoint}/get_url_denylist`, session, params);
    }

    static async updateUrlDenylist(session, params) {
        return AdminClient.put(`${this.endpoint}/update_url_denylist`, session, params);
    }

    static async warnings(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/warnings`, session, params);
    }

    static async teamsToMergePreDraft(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/teams_merge_pre_draft`, session, params);
    }

    static async manageLocationTeams(teamId, session, params) {
        return AdminClient.put(`${this.endpoint}/${teamId}/merge_location_teams`, session, params);
    }

    static async accolades(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/accolades`, session, params);
    }

    static async updateAccoladeTeamId(accoladeId, session, params) {
        return AdminClient.put(`/v1/admin/pwt_accolades/${accoladeId}/update_team_ids`, session, params);
    }

    static async pwtAumAggregations(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/aum_aggregations`, session, params);
    }

    static async addAumAggregation(teamId, session, params) {
        return AdminClient.post(`${this.endpoint}/${teamId}/add_aum_aggregation`, session, params);
    }

    static async removeAumAggregation(teamId, session, params) {
        return AdminClient.delete(`${this.endpoint}/${teamId}/delete_aum_aggregation`, session, params);
    }

    static async patchAumAggregation(teamId, session, params) {
        return AdminClient.patch(`${this.endpoint}/${teamId}/update_aum_aggregation`, session, params);
    }

    static async members(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/team_members`, session, params);
    }

    static async addMember(teamId, session, params) {
        return AdminClient.post(`${this.endpoint}/${teamId}/members`, session, params);
    }

    static async removeMember(teamId, session, params) {
        return AdminClient.delete(`${this.endpoint}/${teamId}/member`, session, params);
    }

    static async removeMembers(session, params) {
        return AdminClient.put(`${this.endpoint}/mark_former_members`, session, params);
    }

    static async potentialTeamMembers(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/potential_team_members`, session, params);
    }

    static async formerTeamMembers(teamId, session, params) {
        return AdminClient.get(`${this.endpoint}/${teamId}/former_team_members`, session, params);
    }

    static async removeFormerMember(teamId, session, params) {
        return AdminClient.delete(`${this.endpoint}/${teamId}/former_team_member`, session, params);
    }

    static async removeFormerMembers(session, params) {
        return AdminClient.put(`${this.endpoint}/delete_former_members`, session, params);
    }

    static async reviveFormerMember(session, params) {
        return AdminClient.put(`${this.endpoint}/revive_member`, session, params);
    }

    static async extraOptions(session, params) {
        return AdminClient.get(`${this.endpoint}/extra_options`, session, params);
    }

    static async contactsByInvestor(session, params) {
        return AdminClient.get(`${this.endpoint}/contacts_by_investor`, session, params);
    }

    static async markAsReviewed(teamId, session) {
        return AdminClient.put(`${this.endpoint}/${teamId}/mark_as_reviewed`, session);
    }

    static async mergeTeams(teamId, session, params) {
        return AdminClient.put(`${this.endpoint}/${teamId}/merge_teams`, session, params);
    }

    static async unmerge(teamId, session) {
        return AdminClient.put(`${this.endpoint}/${teamId}/unmerge`, session);
    }

    static async changeVisibility(session, params) {
        return AdminClient.put(`${this.endpoint}/change_visibility`, session, params);
    }

    static async historicalBlueprints(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}/historical_blueprints`, session);
    }

    static async mergeRelations(teamId, session) {
        return AdminClient.get(`${this.endpoint}/${teamId}/merge_relations`, session);
    }

    static parsedTeam(team) {
        return { team };
    }

    static parsedTeamFilterParams(params) {
        if (!_.isEmpty(params)) {
            return {
                ...params,
                researchers: mapIds(params.researchers || []),
                stage: mapIds(params.stage || []),
                parentCompanies: mapIds(params.parentCompanies || []),
                countries: mapIds(params.countries || []),
                cities: mapIds(params.cities || []),
                states: mapIds(params.states || []),
                services: mapIds(params.services || []),
                associatedContactsCondition: params.associatedContactsCondition?.id,
                status: params.status?.id,
            }
        } else {
            return params;
        }
    }

    static async regenerateAiBio(teamId, session) {
        return AdminClient.put(`${this.endpoint}/${teamId}/regenerate_ai_bio`, session);
    }

    static async removeLocation(teamId, params, session) {
        return AdminClient.delete(`${this.endpoint}/${teamId}/remove_location`, session, params);
    }
}

export default PrivateWealthTeamsService;
